import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Star, Wallet, TriangleAlert, X } from "lucide-react";
import Button from "../common/Button";
import Notice from "../common/Notice";
import useStrategyAutomationFlag from "../../hooks/useStrategyAutomationFlag";
import engineApi from "../../services/engineApi";
import useEngineStatus from "./useEngineStatus";
import EngineModeChip from "./EngineModeChip";
import { isSupernovaTabEnabled } from "../../config/supernovaFlags";
import { goToSupernovaTab } from "../supernova/supernovaNavigation";
import {
  deriveSlots,
  automatedStrategiesFromStatus,
  displayPair,
} from "../supernova/supernovaSlots";
import { compareAllocationEcho } from "./allocationModel";
import { toInlineRejection } from "./configRejection";
import useArbitrumWallet from "./useArbitrumWallet";
import useEnrollment from "./useEnrollment";
import useEngineReport from "./useEngineReport";
import { deriveSlotGate, SLOT_GATE_STATE } from "./slotGate";
import { deriveGateBanner, OPEN_POSITION_RIDING_COPY } from "./gateBanner";
import PerWalletStake from "./PerWalletStake";
import { RiskBanner, DisclosureFooter, DISCLOSURE_DOM_ID } from "./RiskDisclosure";
import AutomationBackdrop from "./AutomationBackdrop";
import AutomationMetricsBand from "./AutomationMetricsBand";
import AutomationSectionLabel from "./AutomationSectionLabel";
import PerformanceSection from "./PerformanceSection";
import WalletSection from "./WalletSection";
import RiskConfigCard, { allowlistChipId, RISK_LIMITS_DOM_ID } from "./RiskConfigCard";
import SignalSetupCard from "./SignalSetupCard";
import StatusPanel from "./StatusPanel";
import TradingViewConnectWizard from "./TradingViewConnectWizard";
import { connectedPairKeys as connectedKeysFromStatus } from "./tradingViewConnect";
import { normalizePairKey } from "../../services/engineApi";
import useWalletRegistry from "./useWalletRegistry";
import WalletRegistryPanel, { WALLET_REGISTRY_DOM_ID } from "./WalletRegistryPanel";
import "./automation.css";

/**
 * Strategy Automation tab — member-facing surface for the non-custodial,
 * bring-your-own-signal auto-trading product (gTrade on Arbitrum).
 *
 * FEATURE FLAG (default OFF — invisible to live members):
 *   strategyAutomationConfig/featureFlags.strategyAutomationTabEnabled
 *   Local preview: REACT_APP_STRATEGY_AUTOMATION_ENABLED=true
 *
 * REAL wiring (existing engine API via services/signalsApi.js — read-only import):
 *   GET /delegate-address, POST /enroll, GET /status, POST /config (the
 *   engine's global risk limits — RiskConfigCard), GET /report and
 *   GET /report/history (performance reporting — display only; each needs its
 *   human-applied signalsApi patch, see patches/signalsApi-getReport.patch and
 *   patches/signalsApi-getReportHistory.patch), plus on-chain Revoke
 *   (removeTradingDelegate) signed in the member's own wallet.
 *
 * Per-pair allocations (#9): PERSISTENCE is real (allocation-only POST /config
 * via the human-applied patches/signalsApi-allocations.patch; values round-trip
 * through /status.riskConfig.allocations). EXECUTION is not: the engine's
 * sizing branch stays behind its audit-gated ALLOCATION_SIZING_ENABLED flag
 * (default OFF), so saved allocations move no money yet.
 *
 * STUBBED (backend doesn't exist yet — see stubData.js, each with
 * TODO(backend, supervised)): multi-wallet, per-position PnL. The staking gate
 * is no longer among them — it reads the engine's slot pool (ARC-53).
 *
 * SUPERNOVA CROSS-LINKS (SUP-6, behind SUPERNOVA_TAB_ENABLED — default OFF):
 * the staked badge opens the Supernova tab, locked rows offer "Use open slot"
 * or a link to go stake, and an allocation with no stake behind it renders as
 * "Paused — slot unbacked" with a re-stake banner. ALL OF IT IS UI STATE. The
 * engine does not enforce the slot gate at signal time yet, so no copy on this
 * tab may say a signal is being blocked. With the flag off, none of it renders
 * and this tab is byte-for-byte the tab members see today.
 *
 * ARC-32 — THE GATE IS LIVE, AND THE SURFACE IS v3:
 *   Locked/unlocked rows and the header badge follow the member's REAL stake,
 *   and regate without a reload when they stake elsewhere and come back.
 *
 * ARC-53 — THE GATE READS /status.staking, AND FAILS CLOSED:
 *   ARC-32 gated on a direct chain read and, when that read failed, fell back to
 *   a placeholder count that UNLOCKED rows. The engine-side gate that was
 *   supposed to backstop it (ARC-43) is not built, so during an outage a member
 *   could configure allocations their stake does not back.
 *
 *   The source is now the engine's member-level slot pool (ARC-42):
 *   `/status.staking` carries slotsTotal/Used/Available, the gate lifecycle,
 *   the unbacked pairs and the per-wallet breakdown, summed across every
 *   verified wallet on the account — which the single-wallet chain read could
 *   never see. Rows unlock up to `slotsTotal` and NOT ONE MORE, and every state
 *   that is not a complete answer — loading, request failed, stake index
 *   unhealthy — LOCKS instead of unlocking. No placeholder count survives, and
 *   a failure never renders as a zero. See slotGate.js for the state machine.
 *
 *   Still display-only: the engine does not enforce the gate at signal time
 *   yet, and no copy here may imply a signal was blocked.
 *
 *   The visual layer is Strategy_Automation_Mockup_v3.html: an aurora backdrop,
 *   a metrics band with the ROI hero, glass consoles with a pulsing delegate
 *   light, and the disclosures gathered into the footer. `.sa-root` re-points the
 *   arc- design tokens for the tab (automation.css), which is what carries every
 *   existing subcomponent onto the new surface without rewriting its classes.
 *
 * ARC-34 — THE ENGINE CONNECTION, AND DEMO AS A FIRST-CLASS STATE:
 *   Every call now goes through services/engineApi.js, the typed client for the
 *   RATIFIED engine contract; /status is polled by useEngineStatus with backoff
 *   on failure and a pause while the tab is hidden, instead of being read once
 *   on mount and left to rot.
 *
 *   The tab labels itself. `engine.mode` is "demo" (no engine URL, or the
 *   engine has never answered — every figure is the local model), "connecting"
 *   (first read in flight), or "live" (the figures are the member's own). An
 *   outage AFTER a successful read does not revert to demo: the numbers are
 *   still the engine's, just old, and that is `stale` — relabelling real money
 *   as an illustration would be as wrong as the reverse. EngineModeChip states
 *   whichever one it is, persistently, in the header.
 *
 *   PLAN-status contract items are NOT called: engineApi's shadow-sizing and
 *   arming stubs throw before touching the transport. Nothing on this tab arms
 *   anything, and nothing here flips a flag.
 *
 * SUPERBETA-8 — CONNECTING TRADINGVIEW IN THREE STEPS:
 *   Every unlocked row offers the connect wizard, and a row the engine has
 *   received a signal for says so and offers the same wizard as a manage
 *   screen. "Connected" is never a local flag: it is /status.recentSignals,
 *   the engine's own record. See TradingViewConnectWizard + tradingViewConnect.
 *   The wizard signs nothing — a signal source is a copy and a paste, and the
 *   only signing on this tab remains the member's own Revoke.
 *
 * Onboarding stays in the supervised flow: this tab contains no
 * setTradingDelegate and no USDC approve. Everything here sits behind the
 * feature flag (default OFF), invisible to live members.
 */

// gTrade diamond on Arbitrum — same fallback as the supervised setup flow.
// The backend's /delegate-address is preferred at runtime; this keeps the
// Revoke safety switch usable even if the backend is unreachable.
const GTRADE_DIAMOND_FALLBACK = "0xFF162c694eAA571f685030649814282eA457f169";

const StrategyAutomationTab = () => {
  const { enabled, loading } = useStrategyAutomationFlag();

  // Flag OFF (or still resolving) ⇒ render nothing at all. Even if a stale
  // shell route ever mounts this component, members see no trace of it.
  if (loading || !enabled) return null;

  return <StrategyAutomationSurface />;
};

const StrategyAutomationSurface = () => {
  const wallet = useArbitrumWallet();
  const reporting = useEngineReport(); // GET /report — read-only display data

  /* ── SUPERBETA-7: the engine connection ─────────────────────────────────
     One hook owns /status: the poll, the backoff, and the demo/live decision.
     `engine.mode` is what the whole tab labels itself with — see
     EngineModeChip. `engine.refresh()` hands back the engine's own answer, so
     the allocation save can compare the echo without racing a re-render. */
  const engine = useEngineStatus();
  const status = engine.status;
  const backendReady = engine.configured;
  const engineLive = engine.mode === "live";
  /* Anything that follows a WRITE — or a member pressing Refresh — forces a
     fresh read rather than being coalesced into whatever poll is in flight.
     A save's echo check compared against a response issued before the save
     would report a stored allocation as unconfirmed. */
  const loadStatus = () => engine.refresh({ force: true });

  const [delegateInfo, setDelegateInfo] = useState(null); // /delegate-address
  const [savingConfig, setSavingConfig] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  /* ARC-54 — where an engine rejection is shown. The banner above is the
     backstop and stays; these two carry the SAME message down to the card the
     member was using, because a message at the top of a tab this long is off
     screen for someone working the risk sliders. Only a 400 lands here — an
     auth failure or an unreachable engine is not a contract rejection and
     stays in the banner alone. */
  const [configRejection, setConfigRejection] = useState(null);
  const [allocationRejection, setAllocationRejection] = useState(null);

  /* /delegate-address once (on-chain constants incl. the diamond for Revoke).
     A failure here is not worth an error banner — the Revoke button falls back
     to the hardcoded diamond below and keeps working. */
  useEffect(() => {
    if (!backendReady) return;
    engineApi.getDelegateAddress().then(setDelegateInfo).catch(() => {});
  }, [backendReady]);

  /* ARC-52: enrollment runs through the ONE shared sequence (enrollFlow, via
     useEnrollment) that SignalsTab and the onboarding wizard already shared.
     This tab used to reach a bare `POST /enroll { wallet }` probe through the
     service layer. The live engine answers that with a 400, never the 428 the
     code was waiting for, so first-time enrollment was already broken — and
     db141f1 has since removed that transport function outright, which would
     have made this a TypeError for every member. The
     sequence is: GET /enroll/nonce → personal_sign the server's message
     verbatim → POST /enroll { wallet, signature, nonce }. Free, off-chain,
     signs no transaction and grants no delegate permission. */
  const enroll = useEnrollment({
    wallet: wallet.account,
    signMessage: wallet.signMessage,
  });
  const enrollment = enroll.enrollment;

  /* connect → status. Enrollment is NOT chained onto the connect click: it
     needs the member's signature, and a signature prompt that appears on its
     own — before they have read what they are signing — is exactly the pattern
     this app refuses elsewhere. Step 2 of the setup card asks for it. */
  const handleConnect = async () => {
    setError(null);
    try {
      await wallet.connect();
      if (backendReady) loadStatus();
    } catch (e) {
      setError(e?.shortMessage || e?.message || "Wallet connection was cancelled.");
    }
  };

  /* SUPERBETA-8 wanted a returning member — wallet already connected, button
     never pressed — to reach step 2 without a dead end. It did that by asking
     /enroll again on mount, which was free only while enrollment was a bare
     unsigned probe. Under SIWE it is not free: it would prompt for a signature
     unbidden. So the trigger is the member's, and the card below carries it. */
  const startEnrollment = enroll.start;

  const handleRevoke = async () => {
    setError(null);
    setNote(null);
    if (
      !window.confirm(
        "Revoke the trading delegate? Automated trading stops immediately. Your funds are unaffected."
      )
    )
      return;
    try {
      await wallet.revokeDelegate(delegateInfo?.diamond || GTRADE_DIAMOND_FALLBACK);
      setNote("Delegate revoked on-chain. Automated trading is off.");
      loadStatus();
    } catch (e) {
      setError(e?.shortMessage || e?.message || "Revoke failed or was rejected.");
    }
  };

  /* save the engine's global risk limits — existing POST /config endpoint */
  const saveRiskConfig = async (cfg) => {
    setError(null);
    setNote(null);
    setConfigRejection(null);
    setSavingConfig(true);
    try {
      await engineApi.setRiskConfig(cfg);
      setNote("Risk settings saved. The engine enforces them before any trade.");
      await loadStatus();
    } catch (e) {
      setError(e.message);
      setConfigRejection(toInlineRejection(e));
    } finally {
      setSavingConfig(false);
    }
  };

  /* save per-pair allocations (#9) — allocation-only partial POST /config.
     Persistence only: saved allocations do NOT size trades until the engine's
     audit-gated ALLOCATION_SIZING_ENABLED flag is armed server-side.
     A transport without the allocations patch applied reports itself
     unsupported and WalletSection keeps its Save disabled — nothing pretends. */
  const allocationsSupported = engineApi.allocationsSupported();
  const [savingAllocations, setSavingAllocations] = useState(false);
  /* SUPERBETA-6: the save is only reported as saved once the ENGINE echoes the
     values back. `/config` has a documented silent-drop failure — it accepted
     unknown fields, returned 200 and discarded them, while the UI echoed local
     state and told the member they were stored. So: post, refetch, compare, and
     hand WalletSection the verdict rather than a bare boolean. */
  const saveAllocations = async (allocations) => {
    setError(null);
    setNote(null);
    setAllocationRejection(null);
    setSavingAllocations(true);
    try {
      await engineApi.setAllocations(allocations);
      const fresh = await loadStatus();
      const echo = compareAllocationEcho(allocations, fresh?.riskConfig?.allocations);
      setNote(
        echo.confirmed
          ? "Allocations saved to your account and confirmed by the engine. They don't trade yet — per-pair allocation sizing ships only after its code audit."
          : "The engine accepted the request but didn't return your allocations, so they are NOT confirmed as stored. Nothing else changed."
      );
      return { ok: true, confirmed: echo.confirmed };
    } catch (e) {
      setError(e.message);
      setAllocationRejection(toInlineRejection(e));
      return { ok: false, confirmed: false };
    } finally {
      setSavingAllocations(false);
    }
  };

  /* ── ARC-46: the multi-wallet registry ──────────────────────────────────
     A member may verify several wallets and segment strategies across them —
     stake on one, trade from another — against the one member-level slot pool.
     Each wallet earns its place with its OWN signature, obtained in the
     member's wallet; nothing here signs and no transaction is constructed.

     `onChanged` refetches /status because the slot pool is summed across
     verified wallets: attaching a wallet that holds staked Supernovas raises
     `slotsTotal`, and detaching one lowers it. The registry list alone cannot
     tell the header that. */
  const walletRegistry = useWalletRegistry({
    enabled: backendReady,
    signMessage: wallet.signMessage,
    requestAccountSelection: wallet.requestAccountSelection,
    onChanged: loadStatus,
  });

  /* Enabled where it can actually work: a transport that speaks /wallets and an
     engine to speak it to. Otherwise it stays disabled and SAYS WHY, which is
     what the old "coming soon" tooltip was standing in for. */
  const canAddWallet = walletRegistry.supported && backendReady;
  const addWalletTitle = walletRegistry.supported
    ? backendReady
      ? "Verify another wallet with its own signature."
      : "Connect the signal engine to manage wallets."
    : "This build can't reach the engine's wallet registry.";

  /* Opening the flow also takes the member to the list it appears in — the
     button lives in the header and the panel is further down the page. */
  const startAddWallet = () => {
    setError(null);
    walletRegistry.beginAdd();
    if (typeof document === "undefined") return;
    const target = document.getElementById(WALLET_REGISTRY_DOM_ID);
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  /* ── SUPERBETA-8: the TradingView connect wizard ────────────────────────
     `tvPair` is the row the wizard is open on, null when it's closed. Which
     strategies count as connected is the ENGINE's answer off
     /status.recentSignals — null while /status hasn't landed, because "nothing
     is connected" and "we haven't asked yet" are different states and only one
     of them should paint an empty state. */
  const [tvPair, setTvPair] = useState(null);
  const tvConnectedKeys = status ? connectedKeysFromStatus(status) : null;
  const tvMode =
    tvPair && tvConnectedKeys?.has(normalizePairKey(tvPair)) ? "manage" : "connect";
  /* The row's own numbers, so step 1 confirms what the member is actually
     looking at rather than a catalog default. */
  const tvAllocationPct = tvPair
    ? status?.riskConfig?.allocations?.[normalizePairKey(tvPair)]?.pct ?? 0
    : 0;

  /* Step 3 polls through the tab's single /status owner rather than opening its
     own connection — one poller, and the rest of the tab sees what the wizard
     sees. */
  const tvPoll = useCallback(() => engine.refresh({ force: true }), [engine]);

  /* The wizard's risk line links here: the tab's full disclosure, which already
     exists at the bottom of the page. Closing first is deliberate — scrolling
     the page behind an open dialog moves something the member can't see. */
  const showDisclosure = () => {
    setTvPair(null);
    if (typeof document === "undefined") return;
    const target = document.getElementById(DISCLOSURE_DOM_ID);
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  /* ── Supernova slot cross-links (SUP-6) ─────────────────────────────────
     Two halves, from two places: the staked count is still the labelled STUB
     (TODO(backend, supervised) — no staking endpoint exists), the automated
     strategies are REAL, straight off /status.riskConfig.pairAllowlist.

     Everything derived here is DISPLAY ONLY. The engine does not enforce the
     slot gate at signal time yet, so nothing below may say a signal was, or
     will be, blocked — only that a slot shows as unbacked.

     All of it is gated on SUPERNOVA_TAB_ENABLED: with the flag off (the default
     and every production build) `supernovaLinked` is false, no cross-link
     renders, and this tab behaves exactly as it does today. */
  const supernovaLinked = isSupernovaTabEnabled();

  /* ── the gate, off the engine's slot pool (ARC-53) ──────────────────────
     `enabled` is the Supernova feature AND a configured engine. Both halves
     matter:
       flag off      ⇒ there is no stake surface for the member to act on, so
                       gating them would be a dead end with no way out. No gate.
       no engine URL ⇒ nothing to ask, and the whole tab is labelled demo.
     With an engine configured, everything short of a complete answer locks —
     including a request that failed outright, which is the case ARC-53 exists
     to fix. */
  const slotGate = deriveSlotGate({
    enabled: supernovaLinked && engine.configured,
    staking: status?.staking || null,
    loading: engine.loading,
    /* ARC-64 — the connection context the payload cannot carry. Without it a
       failed read cannot tell "the engine never answered" apart from "the
       engine answered and served no stake pool", and those need different
       sentences and different buttons. The lock is identical either way. */
    statusSeen: Boolean(status),
    connectionFailing: engine.failures > 0,
    mode: engine.mode,
  });

  /* ARC-64 — one console line per DISTINCT failure, not one per poll. /status
     polls every 45s, so logging unconditionally would bury the evidence it is
     meant to produce under its own repeats. The string names the layer and the
     payload fields that decided it, and carries no wallet address. */
  const loggedDiagnosticRef = useRef(null);
  useEffect(() => {
    const d = slotGate.diagnostic;
    if (!d || d === loggedDiagnosticRef.current) return;
    loggedDiagnosticRef.current = d;
    // eslint-disable-next-line no-console
    console.warn("[slot-gate] stake read unavailable —", d);
  }, [slotGate.diagnostic]);

  /* A number appears on screen only for a complete answer from the engine —
     every other state renders without digits, because a count we could not read
     is not a zero. */
  const stakedCount = slotGate.slotsTotal;

  /* The rail's display model. The allowlist names which pairs sit in the slots;
     the engine's own `slotsUsed` is the fallback for when it named none, so a
     member with no readable allowlist still gets a usage count rather than an
     unknown. */
  const slots = deriveSlots({
    stakedCount,
    strategies: automatedStrategiesFromStatus(status),
    usedCount: slotGate.slotsUsed,
  });

  /* The pairs the engine says have no stake behind them, ready to render.
     null ⇒ no banner: either the gate is "active", or we have no gate to read
     and must not invent one. A non-null empty list is the honest "the engine
     says something is unbacked but did not name it" case, which the banner
     words differently rather than dropping. */
  const unbackedNames =
    slotGate.gate && slotGate.gate !== "active"
      ? slotGate.unbackedPairs.map((p) => displayPair(p.pair))
      : null;

  /* ARC-45 — WHICH banner, off the engine's lifecycle. `closing_only` and
     `blocked` are different sentences because they are different situations,
     and only the engine can tell them apart: the difference is whether a
     position is riding. null ⇒ "active", or a lifecycle we could not read, and
     neither of those is a lapse to announce. See gateBanner.js. */
  const gateBanner = deriveGateBanner({
    gate: slotGate.gate,
    openPositionRiding: slotGate.openPositionRiding,
  });

  /* "Use open slot" changes NO config. It carries the member to the allowlist
     control for that pair — they select it and press Save themselves. */
  const useOpenSlot = (pair) => {
    setError(null);
    setNote(
      `Open slot available — select ${pair} in your pair allowlist below and save your ` +
        "risk settings to automate it. The staking gate is a preview: the engine doesn't " +
        "enforce it yet."
    );
    if (typeof document === "undefined") return;
    const target =
      document.getElementById(allowlistChipId(pair)) ||
      document.getElementById(RISK_LIMITS_DOM_ID);
    if (!target) return;
    if (typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (typeof target.focus === "function") target.focus({ preventScroll: true });
  };

  /* The badge's own copy. A number appears ONLY for a complete answer; the two
     failure states say what happened instead. There is no third option that
     shows a digit — the whole point of ARC-53 is that a number nobody could
     read must never appear on screen as though somebody had. */
  const stakedBadgeText =
    slotGate.state === SLOT_GATE_STATE.READY
      ? `${stakedCount} Supernova staked`
      : slotGate.state === SLOT_GATE_STATE.LOADING
      ? "Checking your stake…"
      : "Stake unavailable";
  const stakedBadgeTitle =
    slotGate.state === SLOT_GATE_STATE.READY
      ? "Your slot pool from the engine, summed across your verified wallets."
      : slotGate.state === SLOT_GATE_STATE.LOADING
      ? "Reading your slot pool from the engine."
      : "We couldn't read your slot pool, so strategies stay locked.";

  return (
    <>
    {/* `.sa-root` is the v3 console surface AND the scope of the token override —
       see automation.css. Everything the tab paints lives inside it. */}
    <div className="sa-root" data-testid="strategy-automation-tab">
      <AutomationBackdrop />

      <div className="sa-wrap">
        {/* ── header ─────────────────────────────────────────────────────── */}
        <div className="sa-top">
          <div>
            <h2 className="sa-h1">Strategy automation</h2>
            <p className="sa-sub">
              Bring your own signals · non-custodial · you keep custody
            </p>
          </div>
          <div className="sa-actions">
            {/* SUPERBETA-7 — demo or live, stated once, always on screen. */}
            <EngineModeChip
              mode={engine.mode}
              stale={engine.stale}
              lastUpdatedMs={engine.lastUpdatedMs}
            />
            {/* The badge is also the way to the stake surface — and the way back
                to it when a row is locked. It renders only where the gate does:
                with no slot pool in play there is no count to state, and the old
                placeholder that used to fill this space is gone (ARC-53). */}
            {slotGate.engaged && (
              <button
                type="button"
                onClick={goToSupernovaTab}
                aria-label="Manage your Supernova stake — opens the Supernova tab"
                title={`${stakedBadgeTitle} Opens the Supernova tab.`}
                className={[
                  "sa-badge-gold",
                  slotGate.state === SLOT_GATE_STATE.READY ? "" : "sa-badge-preview",
                ]
                  .join(" ")
                  .trim()}
                data-testid="sa-staked-badge"
              >
                <Star size={13} aria-hidden="true" />
                {stakedBadgeText} → manage
              </button>
            )}
            {/* ARC-46 — real. The engine's registry (ARC-41) verifies each
                wallet with its own signature; this opens that flow, which
                shows the member the domain before their wallet opens. */}
            <Button
              variant="secondary"
              onClick={startAddWallet}
              disabled={!canAddWallet || walletRegistry.addBusy}
              title={addWalletTitle}
              data-testid="sa-add-wallet"
            >
              <Plus size={15} aria-hidden="true" />{" "}
              {walletRegistry.addBusy ? "Adding wallet…" : "Add wallet"}
            </Button>
          </div>
        </div>

        {/* ── metrics band, ROI hero on the right ─────────────────────────
            Every figure is the engine's own or an em dash — see
            AutomationMetricsBand. No placeholder money anywhere in it. */}
        <AutomationMetricsBand
          report={reporting.report}
          available={reporting.available}
          loading={reporting.loading}
          engineMode={engine.mode}
        />

        {/* ── the gate strip ───────────────────────────────────────────────
            The mockup's gold band. Three renderings, one per gate state, and
            only the third one contains a digit: a skeleton while we ask, an
            error with a retry when we could not, and the member's real slot
            pool when we have it. Cash never needs a slot, so that line is
            unconditional. Nothing renders at all where the gate does not apply.
            (ARC-53 — see slotGate.js.) */}
        {slotGate.engaged && (
          <div className="sa-gate" data-testid="sa-gate-strip">
            <Star size={17} className="text-arc-accent-ink flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="sa-gate-txt">
              Each Supernova staked unlocks <b>1 automated strategy</b>.{" "}
              {slotGate.state === SLOT_GATE_STATE.READY ? (
                <>
                  You have <b>{stakedCount} staked</b>
                  {slots.usageKnown ? (
                    <>
                      {" "}
                      — <b>{slots.used} automated</b> across your wallets
                    </>
                  ) : null}
                  . Cash needs no stake.
                  <span className="block text-caption text-arc-ink-4 mt-1.5">
                    Your slot pool comes from the engine, summed across every
                    wallet you have verified, and refreshes when you come back to
                    this tab. The engine does not enforce the gate at signal time
                    yet, so nothing is being blocked by it.
                  </span>
                </>
              ) : slotGate.state === SLOT_GATE_STATE.LOADING ? (
                <>
                  Cash needs no stake.
                  <span
                    className="sa-skel block mt-1.5"
                    data-testid="sa-gate-skeleton"
                    aria-hidden="true"
                  />
                  <span className="block text-caption text-arc-ink-4 mt-1.5">
                    {slotGate.message}
                  </span>
                </>
              ) : (
                <>
                  Cash needs no stake.
                  <span className="block text-caption text-arc-ink-4 mt-1.5">
                    {slotGate.message}{" "}
                    {/* ARC-64 — the retry appears only where asking again could
                        actually change the answer. An unenrolled wallet, or an
                        engine that serves no stake pool, needs something other
                        than another request, and a button that cannot help
                        reads as "we're fine, keep clicking". */}
                    {slotGate.retryable !== false && (
                      <button
                        type="button"
                        onClick={() => engine.refresh({ force: true })}
                        className="underline font-semibold"
                        data-testid="sa-gate-retry"
                      >
                        Try again
                      </button>
                    )}
                  </span>
                  {/* Which layer failed. The headline above is the member's
                      reassurance; this is the part that makes the failure
                      diagnosable from the screen instead of the console. */}
                  {slotGate.detail && (
                    <span
                      className="block text-caption text-arc-ink-4 mt-1.5"
                      data-testid="sa-gate-detail"
                    >
                      {slotGate.detail}
                    </span>
                  )}
                </>
              )}
            </p>
          </div>
        )}

        {/* ── where the stake sits, wallet by wallet (ARC-45) ───────────────
            Only with a complete answer and 2+ verified wallets — the panel is
            a breakdown OF the pool, so with no readable pool there is nothing
            to break down, and with one wallet it just restates the header. */}
        {slotGate.state === SLOT_GATE_STATE.READY && (
          <PerWalletStake wallets={slotGate.perWallet} />
        )}

        {/* ── pause-on-unstake (display state) ───────────────────────────────
            More strategies allocated than Supernovas staked. The UI shows which
            allocation has no stake behind it; the engine enforces nothing here
            yet, and this banner must not suggest otherwise.

            ARC-38 swept the tab for disclaimer copy above the fold and left this
            one where it is on purpose: it is operational status about THIS
            member's allocation — actionable, conditional, and useless once
            they re-stake — not a standing risk or custody disclaimer. It stays
            at the point of relevance; the bottom block is for the copy that
            renders unconditionally. */}
        {/* ARC-53: the CONDITION is the engine's `gate`, not a comparison this
            component works out. Anything other than "active" means the engine
            has decided the stake no longer covers the allocations, and it names
            which pairs in `unbackedPairs`. Deriving that here from counts would
            be a second opinion — see slotGate.js. */}
        {unbackedNames !== null && (
          <div className="sa-section">
            <Notice tone="warn" role="status" data-testid="supernova-unbacked-banner">
              {/* ARC-45 — the lifecycle sentence, first and on its own line.
                  Both strings are specified verbatim; they live in gateBanner.js
                  so there is one place to change them. */}
              {gateBanner && (
                <span
                  className="block font-semibold text-arc-warn mb-1.5"
                  data-testid="sa-gate-banner-headline"
                >
                  {gateBanner.headline}
                </span>
              )}

              {/* The member with a live position must not read "paused" as
                  "abandoned". The engine is still managing that position to its
                  exit, and `openPositionRiding` is the engine's own answer. */}
              {gateBanner?.riding && (
                <span
                  className="block text-arc-ink-2 mb-1.5"
                  data-testid="sa-gate-banner-riding"
                >
                  {OPEN_POSITION_RIDING_COPY}
                </span>
              )}

              <span className="font-semibold text-arc-warn">
                You have more strategies allocated than Supernovas staked.
              </span>{" "}
              {unbackedNames.length > 0
                ? `${unbackedNames.join(", ")} `
                : "The most recently automated strategy "}
              {unbackedNames.length > 1 ? "show" : "shows"} as{" "}
              <span className="font-semibold">Paused — slot unbacked</span>.{" "}
              <button
                type="button"
                onClick={goToSupernovaTab}
                className="underline font-semibold text-arc-warn"
              >
                Re-stake on the Supernova tab
              </button>{" "}
              to back {unbackedNames.length > 1 ? "them" : "it"} again, or clear{" "}
              {unbackedNames.length > 1 ? "their" : "its"} allocation below.
              <span className="block text-caption text-arc-ink-4 mt-1.5">
                The engine does not enforce the slot gate yet, so nothing is being
                blocked by this.
              </span>
            </Notice>
          </div>
        )}

        {/* errors / notes */}
        <div className="space-y-3">
          {error && (
            <div className="flex items-start gap-2.5 rounded-arc-ctl bg-arc-neg-soft ring-1 ring-arc-neg/40 p-3">
              <TriangleAlert
                size={15}
                className="text-arc-neg flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span className="text-sm text-arc-neg flex-1">{error}</span>
              <button type="button" onClick={() => setError(null)} aria-label="Dismiss error">
                <X size={14} className="text-arc-ink-3" aria-hidden="true" />
              </button>
            </div>
          )}
          {note && <Notice tone="info">{note}</Notice>}
          {/* Demo mode — no base URL, or the engine has never answered. Stated
              in full once here; the chip in the header carries it everywhere
              else. Not an error: the tab is fully usable in this state. */}
          {engine.mode === "demo" && (
            <Notice tone="info" data-testid="sa-demo-notice">
              <span className="font-semibold">Demo data — engine not connected.</span>{" "}
              {backendReady
                ? "The signal engine isn't answering from this environment right now, so the figures below come from a local model rather than your account."
                : "No signal-engine URL is configured in this environment (REACT_APP_SIGNALS_API_URL), so the figures below come from a local model rather than your account."}{" "}
              You can connect your wallet and look around; delegate status,
              collateral, webhook setup and positions need the engine live.
              Nothing here is invented in the meantime — an unknown figure shows
              as an em dash.
            </Notice>
          )}
          {/* Real values that stopped refreshing. Still the member's own — they
              are just older than they look, and saying so is the whole point. */}
          {engine.stale && (
            <Notice tone="warn" role="status" data-testid="sa-stale-notice">
              <span className="font-semibold">Reconnecting to the engine.</span> The
              values below are your real account figures from the last successful
              read — they aren&apos;t updating right now. Retrying automatically,
              less often each time. Nothing about your account has changed.
            </Notice>
          )}
        </div>

        {/* ── performance (real GET /report — read-only, honest empty states) */}
        <section className="sa-section mt-10">
          <AutomationSectionLabel>Portfolio value since inception</AutomationSectionLabel>
          <PerformanceSection
            available={reporting.available}
            report={reporting.report}
            history={reporting.history}
            historyError={reporting.historyError}
            loading={reporting.loading}
            error={reporting.error}
            onRefresh={reporting.refresh}
          />
        </section>

        {/* ── wallet consoles ────────────────────────────────────────────── */}
        <section className="sa-section">
          <AutomationSectionLabel>Crypto trading wallet</AutomationSectionLabel>
          {wallet.account ? (
            <>
              {!wallet.chainOk && (
                <Notice tone="warn" className="mb-4">
                  <span className="font-semibold">Wrong network.</span> Strategy
                  Automation runs on Arbitrum One.{" "}
                  <button
                    type="button"
                    onClick={() =>
                      wallet.switchToArbitrum().catch((e) => setError(e?.message || String(e)))
                    }
                    className="underline font-semibold text-arc-warn"
                  >
                    Switch to Arbitrum
                  </button>
                </Notice>
              )}
              <WalletSection
                account={wallet.account}
                status={status}
                onRevoke={handleRevoke}
                revoking={wallet.busy === "revoke"}
                onSaveAllocations={saveAllocations}
                savingAllocations={savingAllocations}
                allocationsSupported={allocationsSupported}
                slots={slotGate.engaged ? slots : null}
                onUseOpenSlot={useOpenSlot}
                onStakeMore={goToSupernovaTab}
                /* ARC-53 — the whole gate in one prop. `engaged` says whether
                   rows are gated at all; `unlockedCount` is null for every
                   state that is not a complete answer, and null LOCKS. */
                slotGate={slotGate}
                /* SUPERBETA-7 — which of the two the rows are showing, and the
                   per-pair PnL the engine reports on /report (/status carries
                   size and collateral but no PnL). */
                engineLive={engineLive}
                reportPositions={reporting.report?.positions}
                /* SUPERBETA-8 — row entry points + the empty state */
                connectedPairKeys={tvConnectedKeys}
                onConnectTradingView={setTvPair}
                /* ARC-54 — the last allocation save's rejection, shown on the
                   save row as well as in the banner above. */
                rejection={allocationRejection}
              />
              {/* ARC-46 — every wallet the engine has verified, with what each
                  holds and what each is running. The staked counts are the
                  pool's own `perWallet` breakdown, joined by address; a wallet
                  the pool did not count shows an em dash, not a zero. */}
              <div className="mt-5">
                <WalletRegistryPanel
                  registry={walletRegistry}
                  perWallet={slotGate.perWallet}
                />
              </div>
            </>
          ) : (
            <div className="sa-console p-8 text-center">
              <Wallet size={22} className="mx-auto text-arc-accent-ink mb-2" aria-hidden="true" />
              <p className="text-body text-arc-ink-2 mb-4 max-w-md mx-auto">
                Connect your own wallet (Arbitrum One) to see delegate status,
                collateral, and your personal signal webhook. We never see your keys.
              </p>
              <Button onClick={handleConnect} disabled={wallet.connecting}>
                <Wallet size={15} aria-hidden="true" />
                {wallet.connecting ? "Connecting…" : "Connect wallet"}
              </Button>
            </div>
          )}
        </section>

        {/* ── controls (real POST /config, real /enroll, real /status) ───── */}
        <section className="sa-section">
          <AutomationSectionLabel>Risk, signals and engine status</AutomationSectionLabel>
          <div className="space-y-5">
            <RiskConfigCard
              riskConfig={status?.riskConfig}
              onSave={saveRiskConfig}
              saving={savingConfig}
              backendReady={backendReady}
              /* ARC-54 — the last risk save's rejection, on the card itself. */
              rejection={configRejection}
            />
            <SignalSetupCard
              enrollment={enrollment}
              walletConnected={Boolean(wallet.account)}
              onEnroll={startEnrollment}
              enrollBusy={enroll.busy}
              enrollError={enroll.error}
              enrollSupported={enroll.supported}
            />
            <StatusPanel
              status={status}
              onRefresh={loadStatus}
              refreshing={engine.refreshing}
              backendReady={backendReady}
            />
          </div>
        </section>

        {/* ── closing block: custody banner + disclosure ───────────────────
            ARC-38 (Gven's placement rule): ALL disclaimer content lives at the
            BOTTOM of the tab. The top opens with the product — metrics,
            consoles, chart — and carries no warning copy. The banner and the
            footer read as one closing statement: custody strength first, then
            the risk and education clauses. The only disclaimer that stays
            higher up is the one inside the stake/unstake confirm steps, where
            a member must see it at the moment they authorize.
            Persistent compliance surfaces — never remove. */}
        <div className="sa-section">
          <RiskBanner />
        </div>

        <DisclosureFooter />
      </div>
    </div>

      {/* SUPERBETA-8 — OUTSIDE `.sa-root` on purpose. That element isolates its
          stacking context and clips its own overflow for the aurora, both of
          which a fixed-position dialog inside it would inherit the wrong half
          of. The Supernova modals sit outside `.sn-root` for the same reason. */}
      <TradingViewConnectWizard
        open={tvPair !== null}
        pair={tvPair || ""}
        allocationPct={tvAllocationPct}
        enrollment={enrollment}
        onEnroll={startEnrollment}
        enrollBusy={enroll.busy}
        status={status}
        onPoll={tvPoll}
        onClose={() => setTvPair(null)}
        onOpenDisclosure={showDisclosure}
        mode={tvMode}
      />
    </>
  );
};

export default StrategyAutomationTab;
